import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { FORMULA_LABELS, DEFAULT_FORMULAS } from '@itds/Constants';
import { Alert, Button, Loader, Modal, Table } from '@itds/elements';

import EditFormulaDrawer from './Edit';

import Api from '@carrier/utils/Api';
import { groupBy } from 'lodash';

const List = () => {
  const styles = {
    table_label: {
      fontFamily: 'Quicksand',
      fontSize: 18,
      fontWeight: 500,
      marginBottom: 10
    }
  };

  const params = useParams();

  const [formulas, setFormulas] = useState([]);
  const [formula, setFormula] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error_message, setErrorMessage] = useState(null);
  const [success_message, setSuccessMessage] = useState(null);
  const [modal, setModal] = useState(false);
  const [deleteInfo, setDeleteInfo] = useState(null);
  const grouped_formulas = groupBy(formulas, f => f.product_type);

  useEffect(() => {
    loadFormulas();
  }, []);

  const loadFormulas = action => {
    setLoading(true);

    setTimeout(() => {
      setSuccessMessage(null);
      setErrorMessage(null);
    }, 2000);

    Api.loadFormulas({ carrier_id: params.id, data_version_id: params.version_id })
      .then(({ data }) => {
        setFormulas(data);
        setLoading(false);
        if (action === 'add') {
          setSuccessMessage('Formula Successfully Added');
        } else if (action === 'delete') {
          setSuccessMessage('Formula Successfully Deleted');
        }
      })
      .catch(() => {
        if (action === 'add') {
          setErrorMessage('Unable to add Formula at this time');
        } else if (action === 'delete') {
          setErrorMessage('Unable to delete Formula at this time');
        }
      });
  };

  const handleSave = () => {
    setFormula(null);
    loadFormulas('add');
  };

  const handleDelete = (formula, e) => {
    e.stopPropagation();

    window.scrollTo(0, 0);
    setModal(true);
    setDeleteInfo(formula);
  };

  const confirmDelete = () => {
    window.scrollTo(0, 0);
    Api.deleteFormula(deleteInfo.id)
      .then(() => {
        loadFormulas('delete');
        setModal(null);
        setDeleteInfo(null);
      })
      .catch(() => {
        setModal(null);
        setDeleteInfo(null);
      });
  };

  return loading ? (
    <Loader />
  ) : (
    <>
      {success_message ? <Alert type='success'>{success_message}</Alert> : null}
      {error_message ? <Alert type='error'>{error_message}</Alert> : null}
      <EditFormulaDrawer
        formula={formula}
        onClose={setFormula.bind(null, null)}
        onSave={handleSave}
        show={!!formula}
      />

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 30 }}>
        <Button
          onClick={setFormula.bind(null, {
            carrier_id: params.id,
            data_version_id: params.version_id
          })}
        >
          New Formula
        </Button>
      </div>

      {Object.keys(grouped_formulas)
        .sort()
        .map((key, i) => {
          return (
            <div key={i} style={{ marginBottom: 40 }}>
              <h1 key={grouped_formulas[key][0].formula_type} style={styles.table_label}>
                {key} Formulas
              </h1>
              <Table
                columns={[
                  {
                    id: 'product_type',
                    label: 'Product Type'
                  },
                  {
                    id: 'formula_type',
                    label: 'Type',
                    render: f =>
                      `${f.product ? f.product.product_name : 'All Products'} : ${
                        FORMULA_LABELS[f.formula_type]
                      }`,
                    styles: { width: 400 }
                  },
                  {
                    id: 'updated_at',
                    label: 'Last Updated',
                    render: formula => format(new Date(formula.updated_at), 'MM/dd/yyyy')
                  },
                  {
                    id: 'actions',
                    render: formula => {
                      return (
                        <div>
                          <Button
                            color='RED'
                            inline={true}
                            onClick={handleDelete.bind(null, formula)}
                            small={true}
                          >
                            Delete
                          </Button>
                        </div>
                      );
                    },
                    styles: { textAlign: 'right' }
                  }
                ]}
                data={grouped_formulas[key]}
                key={key}
                onRowClick={setFormula}
              />
            </div>
          );
        })}

      <p style={{ marginTop: 40 }}>
        If you don't define a formula for a line item, then it will fall back to these default
        formulas:
      </p>

      <Table
        columns={[
          {
            id: 'name',
            label: 'Line Item',
            styles: { width: 300 }
          },
          {
            id: 'formula',
            label: 'Formula',
            styles: { textAlign: 'right' }
          }
        ]}
        data={Object.keys(DEFAULT_FORMULAS).map(key => {
          return {
            name: key,
            formula: DEFAULT_FORMULAS[key]
          };
        })}
      />

      {modal ? (
        <Modal
          buttons={[
            {
              color: 'RED',
              children: 'Confirm Delete',
              onClick: confirmDelete,
              style: { marginLeft: 'auto' }
            }
          ]}
          contained={true}
          onClose={() => {
            setModal(null);
          }}
          title={`Delete "${FORMULA_LABELS[deleteInfo.formula_type]}"`}
        >
          <div style={{ padding: 40, fontSize: 16, textAlign: 'center', lineHeight: '1.5em' }}>
            Are you sure you want to delete "{FORMULA_LABELS[deleteInfo.formula_type]}"?
          </div>
        </Modal>
      ) : null}
    </>
  );
};

export default List;
